import { Component, ReactNode, ErrorInfo } from "react";
import { SkyText, SkyButton } from "@skypointcloud/skypoint-ui";
import { RotateCw, Home, Copy, CircleAlert } from "lucide-react";
import { ROUTES } from "./constant";
import { authService as auth } from "../../utils/authService";
import { logger } from "../../services/logger.service";
import { v4 as uuidv4 } from "uuid";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, errorId: uuidv4() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    logger.error("React component error caught by ErrorBoundary", error, {
      component: "ErrorBoundary",
      componentStack: errorInfo?.componentStack,
      errorBoundary: true,
      errorId: this.state.errorId,
      errorName: error.name,
      errorMessage: error.message,
    });
  }

  handleReload = (): void => {
    window.location.href = ROUTES.HOME + auth.getQueryString();
  };

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
  };

  handleCopyError = (): void => {
    const { error, errorInfo, errorId } = this.state;
    const errorText = `Error ID: ${errorId}\nError: ${error?.name}: ${error?.message}\nStack: ${error?.stack}\nComponent Stack: ${errorInfo?.componentStack}`;
    navigator.clipboard.writeText(errorText);
  };

  render(): ReactNode {
    const { hasError, error, errorId } = this.state;
    const { fallbackTitle = "Something went wrong" } = this.props;
    const isDev = import.meta.env.DEV;

    if (hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "400px",
            width: "100%",
            gap: "16px",
            padding: "32px",
            textAlign: "center",
            backgroundColor: "var(--sky-bg)",
          }}
        >
          <CircleAlert className="text-red-500 w-16 h-16" />
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <SkyText
              className="font-semibold text-xl dark:text-foreground!"
              style={{ color: "#191919" }}
            >
              {fallbackTitle}
            </SkyText>
            <SkyText
              className="text-sm dark:text-muted-foreground!"
              style={{ color: "#666666", maxWidth: "500px" }}
            >
              We encountered an unexpected error. You can try again or return to the home page.
              {errorId && (
                <span
                  style={{
                    display: "block",
                    marginTop: "8px",
                    fontFamily: "monospace",
                    fontSize: "12px",
                  }}
                >
                  Error ID: {errorId}
                </span>
              )}
            </SkyText>
          </div>

          {isDev && error && (
            <div
              className="dark:bg-red-950/40! dark:border-red-800/60!"
              style={{
                marginTop: "16px",
                padding: "16px",
                backgroundColor: "#FEF2F2",
                borderRadius: "8px",
                maxWidth: "600px",
                textAlign: "left",
                border: "1px solid #FCA5A5",
              }}
            >
              <SkyText
                className="font-semibold text-sm dark:text-red-400!"
                style={{ color: "#DC2626" }}
              >
                {error.name}: {error.message}
              </SkyText>
              {error.stack && (
                <pre
                  className="dark:text-red-300!"
                  style={{
                    marginTop: "8px",
                    fontSize: "11px",
                    color: "#7F1D1D",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: "200px",
                    overflow: "auto",
                  }}
                >
                  {error.stack}
                </pre>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
            <SkyButton
              appearance="primary"
              icon={RotateCw}
              onClick={this.handleRetry}
            >
              Try again
            </SkyButton>
            <SkyButton
              appearance="secondary"
              icon={Home}
              onClick={this.handleReload}
            >
              Go home
            </SkyButton>
            {isDev && (
              <SkyButton
                appearance="subtle"
                icon={Copy}
                onClick={this.handleCopyError}
              >
                Copy error
              </SkyButton>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
